import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, FileText, Receipt, Printer,
  ChevronDown, ChevronUp, Users, IndianRupee, Calendar, Pencil,
  AlertCircle, RefreshCw, Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import {
  getPatient, getPatientPrescriptions, getPatientInvoices,
  getDoctors, queueWalkin, recordInvoicePayment,
  getBillingServices, createBillingInvoice, getPatientDeposits,
} from '@/lib/api';
import { displayAge } from '@/lib/patientAge';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import { PaymentRecordModal } from '@/components/Billing/BillingModals';
import PatientFormModal from '@/components/Patient/PatientFormModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  if (!dateStr) return '';
  return new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'))
    .toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateShort(dateStr: string) {
  if (!dateStr) return '';
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatYear(dateStr: string) {
  if (!dateStr) return '';
  return new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00')).getFullYear().toString();
}

type PaymentMode = 'cash' | 'upi' | 'card' | 'netbanking' | 'cheque' | 'advance' | 'other';
const MODE_MAP: Record<string, PaymentMode> = {
  cash: 'cash', upi: 'upi', card: 'card', cheque: 'cheque',
  netbanking: 'netbanking', 'bank transfer': 'netbanking',
  insurance: 'other', other: 'other',
};

function drName(name: string) {
  return name.startsWith('Dr.') ? name : `Dr. ${name}`;
}

function getInitials(name: string) {
  return name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Invoice status helpers ────────────────────────────────────────────────────

function invoiceRail(status: string) {
  if (status === 'paid')    return 'bg-success';
  if (status === 'partial') return 'bg-warning';
  return 'bg-destructive';
}

function invoiceStatusStyle(status: string) {
  if (status === 'paid')    return 'bg-success/10 text-success';
  if (status === 'partial') return 'bg-warning/10 text-warning';
  return 'bg-destructive/10 text-destructive';
}

// ─── Prescription card ────────────────────────────────────────────────────────

function PrescriptionGroup({ prescriptions, onPrint }: { prescriptions: any[]; onPrint: (rx: any) => void }) {
  const [expanded, setExpanded] = useState(false);
  const primary = prescriptions[0];
  const multiple = prescriptions.length > 1;

  return (
    <div className="group">
      {/* Main row */}
      <div className="flex items-stretch rounded-xl border border-border bg-card overflow-hidden transition-shadow hover:shadow-sm">
        {/* Left color rail — teal for prescriptions */}
        <div className="w-1 flex-shrink-0 bg-primary/70 rounded-l-xl" />

        <div className="flex items-center gap-3 px-3 py-2.5 flex-1 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0">
            <FileText className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">Prescription</span>
              {multiple && (
                <span className="text-[10px] font-bold bg-primary/10 text-primary px-1.5 py-0.5 rounded-full tracking-wide">
                  ×{prescriptions.length}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {primary.doctor_name ? drName(primary.doctor_name) : 'Doctor'}
              {primary.diagnosis && (
                <span className="text-foreground/60"> · {primary.diagnosis}</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-0.5 px-2 flex-shrink-0">
          {multiple && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors"
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          <button
            onClick={() => onPrint(primary)}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Print prescription"
          >
            <Printer className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded sub-prescriptions */}
      {multiple && expanded && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-primary/20 pl-3">
          {prescriptions.map((rx, i) => (
            <div key={rx.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/40 transition-colors">
              <div>
                <p className="text-xs font-medium text-foreground">Prescription {i + 1}</p>
                <p className="text-xs text-muted-foreground">
                  {rx.diagnosis || (rx.medicines?.length ? `${rx.medicines.length} medicine${rx.medicines.length > 1 ? 's' : ''}` : 'No details')}
                </p>
              </div>
              <button
                onClick={() => onPrint(rx)}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <Printer className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FrontDeskPatientPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { can } = usePermission();

  const [patient, setPatient] = useState<any>(null);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);

  // Modals
  const [collectInvoice, setCollectInvoice] = useState<any>(null);
  const [printRx, setPrintRx] = useState<any>(null);
  const [printInvoice, setPrintInvoice] = useState<any>(null);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingServices, setBillingServices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const [patientData, rxData, invData, docData] = await Promise.all([
        getPatient(patientId),
        can('prescriptions:read') ? getPatientPrescriptions(patientId) : Promise.resolve([]),
        can('billing:read')       ? getPatientInvoices(patientId)       : Promise.resolve([]),
        getDoctors(),
      ]);
      if (!patientData) { setError('Patient not found'); return; }
      setPatient(patientData);
      setPrescriptions(Array.isArray(rxData) ? rxData : []);
      setInvoices(Array.isArray(invData) ? invData : []);
      const clinicDocs = (Array.isArray(docData) ? docData : []).filter(
        (d: any) => d.clinic_id === currentUser?.clinic_id
      );
      if (clinicDocs.length) {
        setDoctors(clinicDocs);
        setSelectedDoctorId(prev => prev || clinicDocs[0].id);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load patient';
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [patientId, can]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddToQueue = async () => {
    if (!selectedDoctorId) { toast({ title: 'Select a doctor first', variant: 'destructive' }); return; }
    setQueueLoading(true);
    try {
      await queueWalkin(patientId!, selectedDoctorId, currentUser!.clinic_id!, false);
      toast({ title: 'Added to queue', description: `${patient.name} added to queue` });
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    } finally {
      setQueueLoading(false);
    }
  };

  const handleNewInvoice = async () => {
    setBillingLoading(true);
    try {
      const svcs = await getBillingServices(currentUser!.clinic_id!, currentUser?.doctor_id);
      setBillingServices(Array.isArray(svcs) ? svcs : (svcs as any)?.services ?? []);
    } catch {
      setBillingServices([]);
    } finally {
      setBillingLoading(false);
    }
    setShowBillingModal(true);
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      await createBillingInvoice({
        patient_id:      patientId!,
        doctor_id:       invoiceData.doctorId || selectedDoctorId || currentUser?.doctor_id || '',
        clinic_id:       currentUser!.clinic_id!,
        invoice_date:    invoiceData.invoiceDate || undefined,
        items:           invoiceData.items,
        discount_amount: invoiceData.discount    || 0,
        discount_type:   invoiceData.discount > 0 ? invoiceData.discountType : undefined,
        tax_amount:      invoiceData.taxAmount   || 0,
        notes:           invoiceData.notes       || undefined,
        payments:                     invoiceData.payments    || [],
        balance_forward:              invoiceData.balance_forward              || 0,
        balance_forward_settle:       invoiceData.balance_forward_settle       || 0,
        balance_forward_payment_mode: invoiceData.balance_forward_payment_mode || undefined,
        deposit_amount:               invoiceData.depositAmount      || undefined,
        deposit_payment_mode:         invoiceData.depositPaymentMode || undefined,
      });
      toast({ title: 'Invoice created', description: `Bill created for ${patient.name}` });
      setShowBillingModal(false);
      const invData = await getPatientInvoices(patientId!);
      setInvoices(Array.isArray(invData) ? invData : []);
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    }
  };

  const handleCollectPayment = async (amount: number, method: string, meta?: { reference_number?: string; upi_app?: string; bank_name?: string }) => {
    if (!collectInvoice) return;
    try {
      await recordInvoicePayment(collectInvoice.id, {
        amount,
        payment_mode:     (MODE_MAP[method?.toLowerCase()] ?? 'cash') as PaymentMode,
        payment_date:     new Date().toISOString(),
        reference_number: meta?.reference_number || undefined,
        upi_app:          meta?.upi_app          || undefined,
        bank_name:        meta?.bank_name         || undefined,
      });
      toast({ title: 'Payment recorded', description: `₹${amount} collected` });
      setCollectInvoice(null);
      const invData = await getPatientInvoices(patientId!);
      setInvoices(Array.isArray(invData) ? invData : []);
    } catch (err: unknown) {
      toast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', variant: 'destructive' });
    }
  };

  // ── Computed ──────────────────────────────────────────────────────────────

  const outstanding = invoices
    .filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'partial')
    .reduce((sum, inv) => sum + (Number(inv.total_amount) - Number(inv.paid_amount || 0)), 0);

  const totalBilled = invoices.reduce((sum, inv) => sum + Number(inv.total_amount || 0), 0);

  const groupedVisits = React.useMemo(() => {
    const map: Record<string, { date: string; prescriptions: any[]; invoices: any[] }> = {};
    prescriptions.forEach(rx => {
      const date = (rx.date || rx.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].prescriptions.push(rx);
    });
    invoices.forEach(inv => {
      const date = (inv.invoice_date || inv.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].invoices.push(inv);
    });
    return Object.values(map).sort((a, b) => b.date.localeCompare(a.date));
  }, [prescriptions, invoices]);

  const firstVisitDate = groupedVisits.length > 0
    ? formatDate(groupedVisits[groupedVisits.length - 1].date)
    : null;

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!loading && !can('patients:read')) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <button
          onClick={() => navigate('/frontdesk')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Front Desk
        </button>
        <div className="rounded-2xl border border-border bg-muted/30 p-12 text-center">
          <p className="text-sm text-muted-foreground">You don't have permission to view patient details.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <p className="text-xs text-muted-foreground">Loading patient…</p>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <button
          onClick={() => navigate('/frontdesk')}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Front Desk
        </button>
        <div className="rounded-2xl border border-destructive/20 bg-destructive/5 p-12 text-center">
          <AlertCircle className="w-8 h-8 text-destructive/40 mx-auto mb-3" />
          <p className="text-sm font-medium text-destructive mb-4">{error || 'Patient not found'}</p>
          <Button variant="outline" size="sm" onClick={load} className="gap-2">
            <RefreshCw className="w-3.5 h-3.5" /> Try Again
          </Button>
        </div>
      </div>
    );
  }

  const ageStr = displayAge(patient.age, patient.date_of_birth);
  const initials = getInitials(patient.name);

  return (
    <div className="max-w-2xl mx-auto">

      {/* ── Sticky patient header ──────────────────────────────────────────── */}
      <div
        className="sticky z-10 bg-background/95 backdrop-blur border-b border-border"
        style={{ top: 'var(--topbar-height, 60px)' }}
      >
        {/* Identity row */}
        <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <button
            onClick={() => navigate('/frontdesk')}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          {/* Avatar */}
          <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold flex-shrink-0 tracking-tight">
            {initials}
          </div>

          {/* Name + demographics */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-bold text-foreground capitalize leading-none">{patient.name}</h1>
              {outstanding > 0 && (
                <span className="text-xs font-bold text-destructive bg-destructive/10 px-2 py-0.5 rounded-full flex-shrink-0">
                  ₹{outstanding.toLocaleString('en-IN')} due
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {[ageStr, patient.gender, patient.phone, patient.mrn && `MRN ${patient.mrn}`]
                .filter(Boolean).join(' · ')}
            </p>
          </div>

          {/* Edit button */}
          {can('patients:write') && (
            <button
              onClick={() => setShowEditModal(true)}
              className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              title="Edit patient"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-2 px-4 pb-2.5 flex-wrap">
          {/* Doctor selector */}
          {doctors.length > 1 && (can('queue:write') || can('billing:write')) && (
            <Select value={selectedDoctorId} onValueChange={setSelectedDoctorId}>
              <SelectTrigger className="h-7 text-xs w-32 rounded-lg">
                <SelectValue placeholder="Doctor" />
              </SelectTrigger>
              <SelectContent>
                {doctors.map(d => (
                  <SelectItem key={d.id} value={d.id}>{drName(d.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {can('queue:write') && (
            <Button
              size="sm"
              className="h-7 gap-1.5 text-xs rounded-lg px-3"
              onClick={handleAddToQueue}
              disabled={queueLoading}
            >
              {queueLoading
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Users className="w-3 h-3" />
              }
              {queueLoading ? 'Adding…' : 'Queue'}
            </Button>
          )}
          {can('billing:write') && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs rounded-lg px-3"
              onClick={handleNewInvoice}
            >
              <IndianRupee className="w-3 h-3" /> Invoice
            </Button>
          )}
          {can('appointments:write') && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs rounded-lg px-3"
              onClick={() => setShowBookModal(true)}
            >
              <Calendar className="w-3 h-3" /> Book
            </Button>
          )}
        </div>
      </div>

      {/* ── Stats strip ───────────────────────────────────────────────────── */}
      {(groupedVisits.length > 0 || totalBilled > 0) && (
        <div className="flex items-center gap-4 px-4 py-2.5 border-b border-border/50 bg-muted/20">
          {groupedVisits.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-foreground">{groupedVisits.length}</span>
              <span className="text-xs text-muted-foreground">visit{groupedVisits.length !== 1 ? 's' : ''}</span>
            </div>
          )}
          {firstVisitDate && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">since</span>
              <span className="text-xs font-semibold text-foreground">{firstVisitDate}</span>
            </div>
          )}
          {totalBilled > 0 && (
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-xs text-muted-foreground">billed</span>
              <span className="text-xs font-bold text-foreground">₹{totalBilled.toLocaleString('en-IN')}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Timeline ──────────────────────────────────────────────────────── */}
      <div className="px-4 pb-10">

        {groupedVisits.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <FileText className="w-5 h-5 text-muted-foreground/30" />
            </div>
            <p className="text-sm font-semibold text-muted-foreground">No visit history yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1 max-w-xs">
              Add to queue or create a bill to begin recording visits.
            </p>
          </div>
        ) : (
          groupedVisits.map((visit, i) => {
            const isLatest = i === 0;
            const sameYear = new Date().getFullYear().toString() === formatYear(visit.date);

            return (
              <div key={visit.date} className="pt-4">
                {/* Date separator */}
                <div className="flex items-center gap-3 mb-2.5">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                      {sameYear ? formatDateShort(visit.date) : formatDate(visit.date)}
                    </span>
                    {isLatest && (
                      <span className="text-[10px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
                        Latest
                      </span>
                    )}
                  </div>
                  <div className="flex-1 h-px bg-border" />
                </div>

                {/* Visit items */}
                <div className="space-y-2">
                  {/* Prescriptions */}
                  {can('prescriptions:read') && visit.prescriptions.length > 0 && (
                    <PrescriptionGroup prescriptions={visit.prescriptions} onPrint={setPrintRx} />
                  )}

                  {/* Invoices */}
                  {can('billing:read') && visit.invoices.map(inv => (
                    <div
                      key={inv.id}
                      className="flex items-stretch rounded-xl border border-border bg-card overflow-hidden transition-shadow hover:shadow-sm"
                    >
                      {/* Left rail — color = payment status */}
                      <div className={`w-1 flex-shrink-0 rounded-l-xl ${invoiceRail(inv.payment_status)}`} />

                      <div className="flex items-center gap-3 px-3 py-2.5 flex-1 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                          <Receipt className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">
                              ₹{Number(inv.total_amount).toLocaleString('en-IN')}
                            </span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${invoiceStatusStyle(inv.payment_status)}`}>
                              {(inv.payment_status ?? '').replace(/^\w/, c => c.toUpperCase())}
                            </span>
                          </div>
                          {inv.invoice_number && (
                            <p className="text-xs text-muted-foreground mt-0.5 font-mono">{inv.invoice_number}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1 px-2 flex-shrink-0">
                        {can('billing:write') && (inv.payment_status === 'unpaid' || inv.payment_status === 'partial') && (
                          <button
                            onClick={() => setCollectInvoice(inv)}
                            className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-warning/10 text-warning hover:bg-warning/20 transition-colors"
                          >
                            Collect
                          </button>
                        )}
                        <button
                          onClick={() => setPrintInvoice(inv)}
                          className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="Print invoice"
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            const amount = Number(inv.total_amount).toLocaleString('en-IN');
                            const status = inv.payment_status === 'paid' ? 'fully paid ✓' : `₹${Number(inv.balance_due).toLocaleString('en-IN')} due`;
                            const msg = encodeURIComponent(`Dear ${patient.name},\n\nYour bill (${inv.invoice_number}) for ₹${amount} is ${status}.\n\nThank you.`);
                            const phone = patient.phone?.replace(/\D/g, '');
                            window.open(phone ? `https://wa.me/91${phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank', 'noopener,noreferrer');
                          }}
                          className="p-1.5 rounded-lg hover:bg-success/10 text-muted-foreground hover:text-success transition-colors"
                          title="Share on WhatsApp"
                        >
                          <Share2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────── */}

      {printRx && (
        <PrintPrescriptionPreviewModal open={!!printRx} prescription={printRx} onClose={() => setPrintRx(null)} />
      )}
      {printInvoice && (
        <PrintBillPreviewModal open={!!printInvoice} invoice={printInvoice} onClose={() => setPrintInvoice(null)} />
      )}

      <PaymentRecordModal
        isOpen={!!collectInvoice}
        onClose={() => setCollectInvoice(null)}
        invoice={collectInvoice}
        onRecordPayment={handleCollectPayment}
        loading={false}
      />

      {showBillingModal && (
        <MedicalBillingModal
          isOpen
          onClose={() => setShowBillingModal(false)}
          patient={patient}
          services={billingServices}
          onCreateInvoice={handleCreateInvoice}
          loading={billingLoading}
          clinicId={currentUser!.clinic_id!}
          doctorId={selectedDoctorId || currentUser?.doctor_id || ''}
          doctors={doctors}
        />
      )}

      {showBookModal && (
        <AddAppointmentModal
          isOpen
          onClose={() => setShowBookModal(false)}
          patientId={patientId}
          patients={patient ? [patient] : []}
          showPatientSelection={false}
          onAdded={() => {
            setShowBookModal(false);
            toast({ title: 'Appointment booked' });
          }}
        />
      )}

      {showEditModal && (
        <PatientFormModal
          isOpen
          onClose={() => setShowEditModal(false)}
          onSuccess={async () => {
            setShowEditModal(false);
            const updated = await getPatient(patientId!);
            if (updated) setPatient(updated);
          }}
          mode="edit"
          patientId={patient?.id}
          initialData={{
            name: patient.name ?? '', age: patient.age?.toString() ?? '',
            gender: patient.gender ?? '', phone: patient.phone ?? '',
            phone_2: patient.phone_2 ?? '', email: patient.email ?? '',
            address: patient.address ?? '', city: patient.city ?? '',
            pin_code: patient.pin_code ?? '', blood_group: patient.blood_group ?? '',
            allergies: patient.allergies ?? '', date_of_birth: patient.date_of_birth ?? '',
            preferred_language: patient.preferred_language ?? 'English',
            aadhar_number: patient.aadhar_number ?? '',
            emergency_contact_name: patient.emergency_contact_name ?? '',
            emergency_contact_phone: patient.emergency_contact_phone ?? '',
            payment_preference: patient.payment_preference ?? 'Cash',
          }}
          showPrescriptionOption={false}
          showQueueOption={false}
          title="Edit Patient"
        />
      )}
    </div>
  );
}
